import { useState, useCallback } from "react";
import { GiftService, ReceivedGift, SentGift, GiftPreview, GIFT_NOT_FOUND } from "../ApiGift";

/**
 * Received-gift inbox (Gifting Flavor A). Lists PENDING gifts addressed to the
 * current user and accepts them.
 *
 * SECURITY: accept is only ever constructed from `gifts` (the inbox list) — the
 * accept endpoint 404-hides non-addressees, so we never accept an arbitrary id.
 */
export const useReceivedGifts = () => {
  const [gifts, setGifts] = useState<ReceivedGift[]>([]);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState<number | null>(null);

  const fetchReceivedGifts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GiftService.getReceivedGifts();
      if (response.code === 0) {
        const data = response.data;
        // Tolerate both a bare array and a paginated { results } envelope.
        const list = Array.isArray(data) ? data : data?.results ?? [];
        setGifts(list);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const acceptGift = useCallback(async (giftId: number) => {
    setAccepting(giftId);
    try {
      const response = await GiftService.acceptGift(giftId);
      return response.code === 0;
    } finally {
      setAccepting(null);
    }
  }, []);

  return { gifts, loading, accepting, fetchReceivedGifts, acceptGift };
};

/**
 * Sent-gift list (Gifting Flavor A) — gifts the current user has sent, all
 * statuses. Read-only.
 */
export const useSentGifts = () => {
  const [gifts, setGifts] = useState<SentGift[]>([]);
  const [loading, setLoading] = useState(true);
  const [retracting, setRetracting] = useState<number | null>(null);

  const fetchSentGifts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GiftService.getSentGifts();
      if (response.code === 0) {
        const data = response.data;
        const list = Array.isArray(data) ? data : data?.results ?? [];
        setGifts(list);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Retract a still-PENDING gift I sent. Returns true ONLY on code 0; the caller
  // refetches on every outcome (a lost race / terminal state is already toasted by
  // the interceptor) and shows the success toast only when this is true. Mirrors
  // acceptGift: the id always comes from `gifts`, never an arbitrary value.
  const retractGift = useCallback(async (giftId: number) => {
    setRetracting(giftId);
    try {
      const response = await GiftService.retractGift(giftId);
      return response.code === 0;
    } finally {
      setRetracting(null);
    }
  }, []);

  return { gifts, loading, retracting, fetchSentGifts, retractGift };
};

export interface ClaimResult {
  code: number;
  message: string;
  data: any;
}

/**
 * Share-by-link gift claim (Gifting Flavor C) — the public /presente/<token> page.
 *
 * Both calls resolve rather than throw (the axios interceptor normalizes every
 * error to {data, message, code}), so callers branch on `code`, never try/catch.
 * The token is a bearer credential: it is never logged, toasted or put in an
 * error message.
 */
export const useGiftClaim = (token: string | undefined) => {
  const [preview, setPreview] = useState<GiftPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewCode, setPreviewCode] = useState<number | null>(null);
  const [previewMessage, setPreviewMessage] = useState("");
  const [claiming, setClaiming] = useState(false);

  const fetchPreview = useCallback(async () => {
    if (!token) {
      // No token in the URL — the route can't match without one, but a bare
      // /presente/ would fall through to the catch-all, so fail closed.
      setPreviewCode(GIFT_NOT_FOUND);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await GiftService.getGiftPreview(token);
      setPreviewCode(response.code);
      setPreviewMessage(response.message ?? "");
      if (response.code === 0) setPreview(response.data ?? null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  /**
   * Send the registration OTP for THIS token's claim — POST …/send-code/.
   * Token-bound like `claim`, so RegisterToClaim never handles the bearer
   * credential itself. Resolves to a coded result; the caller branches on `code`.
   */
  const sendCode = useCallback(
    async (phoneNumber: string): Promise<ClaimResult> => {
      if (!token) return { code: -1, message: "", data: null };
      const response = await GiftService.sendClaimCode(token, phoneNumber);
      return {
        code: response.code,
        message: response.message ?? "",
        data: response.data ?? null,
      };
    },
    [token],
  );

  /** Pass no body to claim as the logged-in member; pass the register body otherwise. */
  const claim = useCallback(
    async (body?: {
      phone_number: string;
      password: string;
      birthday: string;
      verification_code: string;
    }): Promise<ClaimResult> => {
      if (!token) return { code: -1, message: "", data: null };
      setClaiming(true);
      try {
        const response = await GiftService.claimGift(token, body);
        return {
          code: response.code,
          message: response.message ?? "",
          data: response.data ?? null,
        };
      } finally {
        setClaiming(false);
      }
    },
    [token],
  );

  return {
    preview,
    loading,
    previewCode,
    previewMessage,
    claiming,
    fetchPreview,
    sendCode,
    claim,
  };
};
